/**
 * Favicon URLs for the app.
 *
 * Icons come from Amoreg's own server (`/api/amoreg/favicon`, session-cookie
 * authenticated), never from a public favicon service: a third-party icon URL
 * would disclose the hostname of every page an agent read for a tenant, from
 * the analyst's browser, to that service. `@amoreg/shared/favicon` decides
 * which hosts may be asked for at all; the server decides what it serves back.
 */
import { FAVICON_ROUTE_PATH, faviconHostForOrigin } from "@amoreg/shared/favicon";

import { primaryHttpUsesBrowserSession } from "~/environments/primary/httpLayer";
import { resolvePrimaryEnvironmentHttpUrl } from "~/environments/primary/target";

/**
 * The favicon URL to render for `rawUrl`, or `null` when the location is
 * unusable or names a private, reserved or internal host — the caller shows
 * its own generic icon then, as it does when the image fails to load.
 */
export function faviconUrlForOrigin(rawUrl: string | null | undefined): string | null {
  const host = faviconHostForOrigin(rawUrl);
  if (host === null) return null;
  try {
    // The route is member-authenticated and an `<img>` carries no bearer, so a
    // session that authenticates with a header rather than the site cookie
    // (the desktop shell, a cross-site backend) would only collect 401s. Show
    // the generic icon instead of making the request — the alternative, a
    // public favicon service, is the disclosure this route exists to remove.
    // This reads the environment target too, so it belongs inside the guard.
    if (!primaryHttpUsesBrowserSession()) return null;
    return resolvePrimaryEnvironmentHttpUrl(FAVICON_ROUTE_PATH, { host });
  } catch {
    // No usable server target (a misconfigured or not-yet-paired client):
    // an icon is never worth failing a render for.
    return null;
  }
}
